import React, { useState, useEffect, useMemo } from 'react';
import { getUniverseCount } from '../api';

// ── Design tokens (neutral, works on light backgrounds) ──────────────────────
const C = {
  border:  '#E2E8F0',
  primary: '#2563EB',
  success: '#16A34A',
  textLg:  '#0F172A',
  textMd:  '#374151',
  textSm:  '#6B7280',
};

// ── Single param cell ─────────────────────────────────────────────────────────
function Cell({ label, value, highlight, dash, title }) {
  const isEmpty = value == null || value === '' || value === false;
  const isTrue  = value === true || value === 'true' || value === '✓';
  const display = isEmpty ? '—' : isTrue ? '✓' : String(value);

  return (
    <span title={title} style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start',
      background: isEmpty || dash ? '#FAFAFA'
                : highlight      ? '#EFF6FF'
                : isTrue         ? '#DCFCE7'
                :                  '#F8FAFC',
      border: `1px solid ${
        isEmpty || dash ? '#E9ECF0'
        : highlight     ? '#BFDBFE'
        : isTrue        ? '#BBF7D0'
        :                 C.border
      }`,
      borderRadius: 5, padding: '3px 7px', fontSize: 10.5,
      fontFamily: "'SFMono-Regular','Consolas','Liberation Mono',monospace",
      opacity: isEmpty ? 0.55 : 1,
    }}>
      <span style={{ color: C.textSm, fontSize: 9.5, lineHeight: 1.2, marginBottom: 1 }}>{label}</span>
      <span style={{
        fontWeight: 600,
        color: isEmpty ? C.textSm : highlight ? C.primary : isTrue ? C.success : C.textLg,
      }}>
        {display}
      </span>
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.7px', color: C.textSm, marginBottom: 4,
      }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {children}
      </div>
    </div>
  );
}

// ── Helper to format sell window ──────────────────────────────────────────────
function fmtWindow(val, dataType) {
  if (val == null) return null;
  if (dataType === 'daily') return `${val}d`;
  if (dataType === '5m')    return `${val}×5m`;
  if (dataType === '1m')    return `${val}×1m`;
  return `${val}h`;
}

// ── Filter helper: keep only items with a real value ─────────────────────────
function hasVal(v) {
  return v != null && v !== '' && v !== false;
}

// ── Universe helpers ─────────────────────────────────────────────────────────
// Index keys → labels (mirror SP_INDICES in StrategyAnalyzer.js).
const INDEX_LABELS = {
  SP500:  'S&P 500',
  SP400:  'S&P 400',
  SP600:  'S&P 600',
  NDX:    'NASDAQ 100',
  NASDAQ: 'NASDAQ Composite',
};

// Compact money: 200000000000 → "$200B"
function fmtCap(v) {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return '0';
  for (const [div, suf] of [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']]) {
    if (n >= div) {
      const x = n / div;
      return `$${(x >= 100 ? Math.round(x) : Number(x.toFixed(1)))}${suf}`;
    }
  }
  return `$${n}`;
}

// A list-valued filter: [] means "all", a missing key means "unknown" (→ null).
function listLabel(val, mapLabel) {
  if (val == null) return null;
  const arr = Array.isArray(val) ? val : [val];
  if (!arr.length) return 'All';
  return arr.map(x => (mapLabel ? (mapLabel[x] || x) : x)).join(', ');
}

// Market cap: multi-select buckets (+ optional custom range), else the raw
// min..max pair, else "All" when the range covers everything.
function capLabel(p) {
  const buckets = Array.isArray(p.market_cap_buckets) ? p.market_cap_buckets : null;
  const min = p.market_cap_min;
  const max = p.market_cap_max;
  const rangeStr = (min != null || max != null)
    ? `${fmtCap(min ?? 0)}–${max != null && Number(max) < 1e15 ? fmtCap(max) : '∞'}`
    : null;

  if (buckets && buckets.length) {
    const presets  = buckets.filter(b => String(b).toLowerCase() !== 'custom');
    const hasCust  = buckets.some(b => String(b).toLowerCase() === 'custom');
    const parts    = [...presets];
    if (hasCust && rangeStr) parts.push(rangeStr);
    else if (hasCust)        parts.push('custom');
    return parts.join(' + ');
  }

  if (min == null && max == null) return p.market_cap_type || null;
  // Legacy single-range rows: the "everything" range is the All state.
  if (Number(min || 0) <= 0 && Number(max ?? 0) >= 1e15) return 'All';
  return rangeStr;
}

// ── Universe size ────────────────────────────────────────────────────────────
// The row stores its FILTERS, not the resulting ticker list, so the size is asked
// from the same endpoint the search form's "N тікерів" counter uses (identical
// _build_ticker_list on the backend). It is therefore today's roster — the count
// can drift from the run day as listings/marginability change.
const _UNI_CACHE = new Map();

// Request body for the row, or null when the count makes no sense:
// a row that carries no universe fields at all (live-bot params), or a
// specific-ticker list (its size is already spelled out in the cell).
function universeQuery(p) {
  if (!p) return null;
  const picked = Array.isArray(p.specific_tickers) ? p.specific_tickers : null;
  const known  = picked != null || p.ticker_mode != null;
  if (!known || (picked && picked.length)) return null;
  const arr = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
  return {
    data_type:          p.data_type || 'hourly',
    country:            arr(p.country ?? p.countries),
    sector:             arr(p.sector  ?? p.sectors),
    index:              arr(p.index   ?? p.indexes),
    marginable_only:    !!p.marginable_only,
    market_cap_min:     p.market_cap_min ?? null,
    market_cap_max:     p.market_cap_max ?? null,
    market_cap_buckets: arr(p.market_cap_buckets),
    beta_min:           p.beta_min ?? null,
    beta_max:           p.beta_max ?? null,
    min_avg_volume:     p.min_avg_volume ?? null,
    specific_tickers:   [],
  };
}

function useUniverseCount(params) {
  const key = useMemo(() => {
    const q = universeQuery(params);
    return q ? JSON.stringify(q) : null;
  }, [params]);

  const [count, setCount] = useState(() => (key ? _UNI_CACHE.get(key) ?? null : null));

  useEffect(() => {
    if (!key) { setCount(null); return; }
    if (_UNI_CACHE.has(key)) { setCount(_UNI_CACHE.get(key)); return; }
    let alive = true;
    getUniverseCount(JSON.parse(key))
      .then(r => {
        const n = r?.data?.count;
        if (typeof n !== 'number') return;
        _UNI_CACHE.set(key, n);
        if (alive) setCount(n);
      })
      .catch(() => {});   // no data dir / no access → cell keeps its plain label
    return () => { alive = false; };
  }, [key]);

  return count;
}

// ── Main component ─────────────────────────────────────────────────────────────
// props:
//   params  — strategy params object (from backtest result or running bot)
//   compact — if true, only show core section; default false
export default function StrategyParamsLabel({ params, compact = false }) {
  // Hooks first — the early return below must not change their order.
  const universeSize = useUniverseCount(params);

  if (!params) return null;
  const p = params;
  const rp = p.risk_protection || {};
  const dt = p.data_type;

  // ── 1. Core trading params ────────────────────────────────────────────────
  // Розмір позиції у просунутому рушії — ПОХІДНЕ, а не керуючий параметр:
  // рушій рахує слот як капітал ÷ кількість компаній, а поле `pos_size`
  // `simulate_advanced` не приймає взагалі (бот сюди ж — `get_position_size_usd`
  // через `max_positions`). Показувати його окремим числом означало б видавати
  // похідне за кероване; сам параметр лишається в params_json заради сумісності.
  const isAdvanced = String(p.strategy || '') === 'advanced';
  const nPos       = p.num_companies ?? p.max_positions;
  const slotUsd    = (p.initial_capital != null && Number(nPos) > 0)
    ? Number(p.initial_capital) / Number(nPos)
    : null;
  const slotCell = isAdvanced
    ? {
        label: 'Слот',
        value: Number(nPos) > 0
          ? `капітал ÷ ${nPos}${slotUsd != null ? ` ≈ $${Math.round(slotUsd).toLocaleString()}` : ''}`
          : null,
        highlight: true,
        title: 'Похідне значення: просунутий рушій рахує розмір позиції як '
             + 'капітал ÷ кількість компаній. Окремого параметра «розмір позиції» тут немає.',
      }
    : { label: 'Pos Size%', value: p.pos_size ?? p.pos_size_pct, highlight: true };

  const core = [
    { label: 'Data Type',      value: p.data_type,       highlight: true },
    { label: 'Start',          value: p.start_time },
    { label: 'End',            value: p.stop_time },
    { label: 'Capital',        value: p.initial_capital != null ? `$${Number(p.initial_capital).toLocaleString()}` : null },
    { label: 'Buy%',           value: p.x_buy,           highlight: true },
    { label: 'T-Buy',          value: fmtWindow(p.t_hours_buy ?? p.buy_window_days, dt), highlight: true },
    { label: 'Sell%',          value: p.y_sell,          highlight: true },
    { label: 'Sell Window',    value: fmtWindow(p.sell_window_days ?? p.sell_window_hours, dt) },
    { label: 'Sell Mode',      value: p.sell_mode ?? 'rolling' },
    { label: 'Hard Stop%',     value: p.hard_stop_pct != null && Number(p.hard_stop_pct) > 0 ? p.hard_stop_pct : null },
    { label: 'HS Cooldown',    value: p.hard_stop_cooldown_bars != null && Number(p.hard_stop_cooldown_bars) > 0 ? p.hard_stop_cooldown_bars : null },
    slotCell,
    { label: 'Max Pos',        value: p.num_companies ?? p.max_positions, highlight: true },
    { label: 'Reinvest',       value: p.reinvest != null ? String(p.reinvest) : null },
    { label: 'Peak Buy Win',   value: p.use_peak_window_buy  ? '✓' : null },
    { label: 'Peak Sell Win',  value: p.use_peak_window_sell ? '✓' : null },
  ];

  // ── 2. Universe & Filters ─────────────────────────────────────────────────
  // Support both params keys and _meta keys (merged via spread in caller)
  // Everything the search form's "Всесвіт та фільтри" block can set is shown —
  // including the "All" states (empty sector/index list = no restriction) and the
  // marginable flag, so a saved row states its universe in full.
  const _country  = p.country  ?? p.countries;
  const _sector   = p.sector   ?? p.sectors;
  const _industry = p.industry;
  const _index    = p.index    ?? p.indexes;
  const _regimes  = p.market_regimes ?? p.regime_filter;
  const _tickers  = Array.isArray(p.specific_tickers) ? p.specific_tickers : [];
  const _betaMin  = p.beta_min;
  const _betaMax  = p.beta_max;
  const _minVol   = p.min_avg_volume;
  const _specific = _tickers.length > 0;

  const universe = [
    { label: 'Тікери',     value: _specific
        ? `🎯 ${_tickers.length}: ${_tickers.slice(0, 12).join(', ')}${_tickers.length > 12 ? ` +${_tickers.length - 12}` : ''}`
        // "all" mode is only claimed when the row really carries the field.
        // universeSize > 0 = live count for these filters (see useUniverseCount).
        : (Array.isArray(p.specific_tickers) || p.ticker_mode === 'all')
            ? (universeSize > 0 ? `${universeSize} по фільтрах` : 'Всі по фільтрах')
            : null,
      title: _specific ? _tickers.join(', ')
           : universeSize > 0 ? 'Скільки тікерів проходить ці фільтри зараз — той самий лічильник, що у формі пошуку. З часом може відрізнятись від дня запуску (лістинги, маржинальність).'
           : undefined },
    { label: 'Country',    value: listLabel(_country) },
    { label: 'Sector',     value: listLabel(_sector) },
    { label: 'Industry',   value: Array.isArray(_industry) && _industry.length ? _industry.join(', ') : null },
    { label: 'Index',      value: listLabel(_index, INDEX_LABELS) },
    { label: 'Market Cap', value: capLabel(p) },
    { label: 'Marginable', value: p.marginable_only == null ? null : (p.marginable_only ? '✓' : 'Ні') },
    { label: 'Beta Min',   value: _betaMin != null && Number(_betaMin) > 0 ? _betaMin : null },
    { label: 'Beta Max',   value: _betaMax != null && Number(_betaMax) > 0 ? _betaMax : null },
    { label: 'Min Volume', value: _minVol > 0 ? `${(_minVol / 1e6).toFixed(1)}M` : null },
    { label: 'Regime',     value: Array.isArray(_regimes) ? (_regimes.length ? _regimes.join(', ') : null) : (_regimes || null) },
  ];

  // ── 3. Intraday ───────────────────────────────────────────────────────────
  const intraday = [
    { label: 'Enabled',     value: p.intraday_enabled ? '✓' : null },
    { label: 'Intraday%',   value: p.intraday_enabled ? (p.intraday_pct ?? null) : null },
  ];

  // ── 4. Stagnation ─────────────────────────────────────────────────────────
  const stagnation = [
    { label: 'Enabled',    value: p.stagnation_enabled ? '✓' : null },
    { label: 'Stag Min%',  value: p.stagnation_enabled ? (p.stagnation_min_pct ?? null) : null },
    { label: 'Stag Max%',  value: p.stagnation_enabled ? (p.stagnation_max_pct ?? null) : null },
    { label: 'Stag Bars',  value: p.stagnation_enabled ? (p.stagnation_bars ?? null) : null },
  ];

  // ── 5. Risk Protection ────────────────────────────────────────────────────
  const risk = [
    { label: 'Enabled',        value: rp.enabled ? '✓' : null },
    { label: 'Sell Overnight', value: rp.enabled ? (rp.sell_overnight ? '✓' : null) : null },
    { label: 'Sell Weekend',   value: rp.enabled ? (rp.sell_weekend   ? '✓' : null) : null },
    { label: 'Max Loss%',      value: rp.enabled && rp.max_loss_to_sell_pct > 0 ? rp.max_loss_to_sell_pct : null },
  ];

  // ── 6. Buy Filters ────────────────────────────────────────────────────────
  const buyFilters = [
    { label: 'Buy Cap%',       value: p.buy_cap_pct > 0 ? p.buy_cap_pct : null },
    { label: 'Overpriced%',    value: p.overpriced_pct > 0 ? p.overpriced_pct : null },
    { label: 'Overpriced Bars',value: p.overpriced_period_bars > 0 ? p.overpriced_period_bars : null },
    { label: 'Dip Filter',     value: p.dip_filter_enabled ? '✓' : null },
    { label: 'Dip%',           value: p.dip_filter_enabled && p.dip_pct != null ? p.dip_pct : null },
    { label: 'Dip Bars',       value: p.dip_filter_enabled && p.dip_period_bars != null ? p.dip_period_bars : null },
  ];

  // ── 7. Advanced Mechanisms ─────────────────────────────────────────────────
  const advanced = [
    { label: 'Rotation',       value: p.rotation_enabled ? '✓' : null },
    { label: 'Rot Min Bars',   value: p.rotation_enabled ? (p.rotation_min_hold_bars ?? null) : null },
    { label: 'Rot Factor',     value: p.rotation_enabled ? (p.rotation_factor ?? null) : null },
    { label: 'Opportunity',    value: p.opportunity_enabled ? '✓' : null },
    { label: 'Opp Min Bars',   value: p.opportunity_enabled ? (p.opportunity_min_hold_bars ?? null) : null },
    { label: 'Opp Gain%',      value: p.opportunity_enabled ? (p.opportunity_min_gain_pct ?? null) : null },
    { label: 'Продаж під покупку', value: p.swap_defer_enabled ? '✓' : null },
    { label: 'Частка TP%',     value: Number(p.swap_tp_frac_pct || 0) > 0 ? p.swap_tp_frac_pct : null },
    { label: 'HTC під покупку', value: p.swap_htc_enabled ? '✓' : null },
    { label: 'Buy% на виході', value: (p.strategy || '') !== 'advanced' ? null
        : (p.no_growth_enabled === false || p.no_growth_enabled === 'false') ? 'лише вхід' : 'вхід + утримання' },
    { label: 'Hold-to-Cost',   value: p.hold_to_cost_enabled ? '✓' : null },
    { label: 'H2C Bars',       value: p.hold_to_cost_enabled ? (p.hold_to_cost_bars ?? null) : null },
    { label: 'H2C беззбитк.',  value: p.hold_to_cost_enabled && (p.htc_cost_enabled === true || p.htc_cost_enabled === 'true')
        ? `+${p.htc_cost_markup_pct ?? 0.1}%` : null },
    { label: 'TP Mode',        value: p.take_profit_mode > 0 ? (p.take_profit_mode === 1 ? 'immediate' : 'let it run') : null },
    { label: 'TP%',            value: p.take_profit_mode > 0 ? (p.take_profit_pct ?? null) : null },
    { label: 'TP Expiry',      value: p.take_profit_mode > 0 && p.take_profit_expiry_bars > 0 ? `${p.take_profit_expiry_bars}b` : null },
  ];

  // ── 8. Шари реалізму (блоки 5-10 форми пошуку) ────────────────────────────
  // Показуємо ЯВНО і увімкнені, і вимкнені: «—» тут значуще. Рядок без обігу
  // чи без делістингів рахований старими правилами, і при десятках прогонів на
  // день це єдиний спосіб згадати, що саме було задано в цьому пошуку.
  const realismOn = k => (p[k] == null ? null : (p[k] ? '✓' : 'Ні'));
  const realism = [
    { label: 'Обіг (ліквідн.)', value: realismOn('liquidity_enabled') },
    { label: 'Мін. обіг/день',  value: p.liquidity_enabled && p.min_adv_usd > 0
        ? `$${(Number(p.min_adv_usd) / 1e6).toFixed(2)}M` : null },
    { label: 'Ліміт заявки',    value: p.liquidity_enabled && p.position_adv_cap_pct > 0
        ? `${p.position_adv_cap_pct}% обігу` : null },
    { label: 'Вікно обігу',     value: p.liquidity_enabled ? (p.adv_window_days ?? null) : null },
    { label: 'Порожніх барів',  value: p.liquidity_enabled && p.max_flat_bar_pct != null
        ? `≤${p.max_flat_bar_pct}%` : null },
    { label: 'Плата за вплив',  value: p.liquidity_enabled ? realismOn('impact_enabled') : null },
    { label: 'Вплив, б.п./%',   value: p.liquidity_enabled && p.impact_enabled
        ? (p.impact_bps_per_pct ?? null) : null },
    { label: 'Архівна кап.',    value: realismOn('pit_universe_enabled') },
    { label: 'Без даних кап.',  value: p.pit_universe_enabled
        ? (p.pit_require_data ? 'не кандидат' : 'відкат на сьогодні') : null },
    { label: 'Делістинги',      value: realismOn('delisting_enabled') },
    { label: 'Зміна компанії',  value: realismOn('reorg_enabled') },
    { label: 'Лише прості акції', value: realismOn('common_stock_only') },
    { label: 'Всесвіт по барах',value: realismOn('per_bar_universe') },
    { label: 'Дроблення заявок',value: realismOn('slicing_enabled') },
    { label: 'Мін. частина',    value: p.slicing_enabled && p.min_position_usd > 0
        ? `$${p.min_position_usd}` : null },
    { label: 'Мін. ціна акції', value: p.slicing_enabled && p.min_share_price > 0
        ? `$${p.min_share_price}` : null },
    { label: 'Частк. продажі',  value: realismOn('partial_sells_enabled') },
    { label: 'Динамічне N',     value: realismOn('dynamic_n_enabled') },
    { label: 'База ділення',    value: p.dynamic_n_enabled
        ? (Number(p.dynamic_n_mode) === 1 ? 'лише акції' : 'акції + кеш') : null },
    // Осі менеджера 2026-08-19. Пріоктет показуємо ЗАВЖДИ (у т.ч. «падіння» —
    // саме воно діяло в кожному рядку до цієї фічі), набір кап. — лише коли
    // класи справді обрані: порожньо означає «без фільтра класу».
    { label: 'Пріоритет купівлі', value: ({
        dip: 'глибина падіння', turnover: 'більший обіг', spike: 'сплеск обігу',
      }[p.buy_priority || 'dip'] || p.buy_priority) },
    { label: 'Набір кап.',      value: Array.isArray(p.market_cap_buckets) && p.market_cap_buckets.length
        ? p.market_cap_buckets.join('+') : null },
  ];

  // ── 9. Маржинальне плече ──────────────────────────────────────────────────
  const margin = [
    { label: 'Плече',        value: p.margin_enabled ? `×${p.margin_cap}` : null },
    { label: 'Підтримка%',   value: p.margin_enabled ? (p.margin_maintenance_pct ?? null) : null },
    { label: 'Делев. старт%',value: p.margin_enabled ? (p.margin_delev_start_pct ?? null) : null },
    { label: 'Делев. стоп%', value: p.margin_enabled ? (p.margin_delev_stop_pct ?? null) : null },
    { label: 'APR%',         value: p.margin_enabled ? (p.margin_interest_apr_pct ?? null) : null },
    { label: 'Мін. ціна',    value: p.margin_enabled && p.margin_min_price > 0
        ? `$${p.margin_min_price}` : null },
  ];

  // For compact mode — show only core params that have a value
  if (compact) {
    const visibleCore = core.filter(x => hasVal(x.value));
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {visibleCore.map(({ label, value, highlight, title }) => (
          <Cell key={label} label={label} value={value} highlight={highlight} title={title} />
        ))}
      </div>
    );
  }

  // Filter each section to items with real values only
  const coreV       = core.filter(x => hasVal(x.value));
  const universeV   = universe.filter(x => hasVal(x.value));
  const intradayV   = intraday.filter(x => hasVal(x.value));
  const stagnationV = stagnation.filter(x => hasVal(x.value));
  const riskV       = risk.filter(x => hasVal(x.value));
  const buyFiltersV = buyFilters.filter(x => hasVal(x.value));
  const advancedV   = advanced.filter(x => hasVal(x.value));
  const realismV    = realism.filter(x => hasVal(x.value));
  const marginV     = margin.filter(x => hasVal(x.value));

  return (
    <div>
      {/* ── Core ── */}
      {coreV.length > 0 && (
        <Section title="Параметри торгівлі">
          {coreV.map(({ label, value, highlight, title }) => (
            <Cell key={label} label={label} value={value} highlight={highlight} title={title} />
          ))}
        </Section>
      )}

      {/* ── Universe & Filters ── */}
      {universeV.length > 0 && (
        <Section title="Universe &amp; Filters">
          {universeV.map(({ label, value, title }) => (
            <Cell key={label} label={label} value={value} title={title} />
          ))}
        </Section>
      )}

      {/* ── Intraday ── */}
      {intradayV.length > 0 && (
        <Section title="Intraday сигнал">
          {intradayV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Stagnation ── */}
      {stagnationV.length > 0 && (
        <Section title="Stagnation">
          {stagnationV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Risk Protection ── */}
      {riskV.length > 0 && (
        <Section title="Risk Protection">
          {riskV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Buy Filters ── */}
      {buyFiltersV.length > 0 && (
        <Section title="Buy Filters">
          {buyFiltersV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Advanced Mechanisms ── */}
      {advancedV.length > 0 && (
        <Section title="Advanced Mechanisms">
          {advancedV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Шари реалізму ── */}
      {realismV.length > 0 && (
        <Section title="Реалізм (обіг, архівна кап., делістинги, дроблення)">
          {realismV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* ── Маржинальне плече ── */}
      {marginV.length > 0 && (
        <Section title="Маржинальне плече">
          {marginV.map(({ label, value }) => (
            <Cell key={label} label={label} value={value} />
          ))}
        </Section>
      )}

      {/* Переліку періодів тут БІЛЬШЕ НЕМАЄ (прибрано 08.09 на запит менеджера).
          Дві причини, і обидві самостійні:

          1) він був зламаний від початку — читав `pr.start`/`pr.end`, тоді як
             записи `additional_periods` звуться `start_date`/`end_date`. Тобто
             кожен рядок друкувався як «P1: –», і чим більше вікон, тим довший
             стовпчик прочерків;
          2) навіть полагоджений, він дублює таблицю періодів, у якій ті самі
             вікна стоять із метриками. При сотні вікон це сто рядків шуму над
             кожним параметром стратегії.

          Кількість вікон видно в колонці «Periods» і в самій таблиці. */}
    </div>
  );
}
